import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { isBefore } from 'date-fns';
import { useSnackbar } from '../snackbar/hooks';
import { indexQuestionnaires } from '../questionnaires/questionnairesSlice';
import { indexContacts } from '../contacts/contactsSlice';
import { indexContracts } from '../contracts/contractsSlice';
import { createQuestionnaireToken } from '../questionnaires/utils';
import { storeNurseRequests, closeNurseRequests, indexNurseRequests } from './nurseRequestsSlice';
import { destroyCareNeed, showCareNeed } from '../careNeeds/careNeedsSlice';
import { useNurseDeploymentsByCareNeedId } from '../nurseDeployments/nurseDeploymentsSlice';

/**
 * Shared by SendNurseRequestButton (fresh send) and MailDraftActions (continuing
 * a saved draft), so a request saved as a draft and sent later still stores a
 * NurseRequest per selected agency (via a single bulk request) instead of
 * silently sending mail only.
 */
export const useNurseRequestSend = (customerId, careNeedId) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const prepareExtraEntities = useCallback(() => {
        /*
         * Somewhat hacky but gets the job done..
         *
         * A better way to do this would be to extract the useFooById-hook-functionality into
         * a separate async function (that doesn't use hooks) that can be used here
         * (to prevent unnecessary loading of contacts and questionnaires
         * if they are present already in the redux store anyway)
         * */
        return dispatch(indexContacts({ customerId, isPatient: true }))
            .then(({ data: patientContacts }) =>
                Promise.all(
                    patientContacts.map((patientContact) =>
                        dispatch(indexQuestionnaires({ contactId: patientContact.id }))
                    )
                )
            )
            .then((questionnairesByContacts) => {
                const result = {
                    questionnaires: questionnairesByContacts
                        .map(({ data: questionnairesByContact }) =>
                            questionnairesByContact.reduce((carry, _questionnaire) => {
                                if (!carry) {
                                    return _questionnaire;
                                }
                                return isBefore(
                                    new Date(carry.createdAt),
                                    new Date(_questionnaire.createdAt)
                                )
                                    ? _questionnaire
                                    : carry;
                            }, null)
                        )
                        .reduce(
                            (carry, questionnaire) => ({
                                ...carry,
                                [questionnaire.contactId]: questionnaire,
                            }),
                            {}
                        ),
                };
                const querstionnairsarray = Object.values(result.questionnaires);

                return Promise.all([
                    result,
                    ...querstionnairsarray.map((quest) =>
                        createQuestionnaireToken(
                            customerId,
                            quest.contactId,
                            false,
                            null,
                            dispatch,
                            1
                        )
                    ),
                ]);
            })
            .then((ret) => {
                const quests = ret.shift();
                quests.tags = ret.map((token, index) => ({
                    tag: 'EBLINKANONYM' + (index ? index + 1 : ''),
                    value: token,
                }));
                const combinedString = quests.tags.map((tag) => `${tag.value}`).join('\n');
                var allPatientLinksCombined = [];
                allPatientLinksCombined.push({ tag: 'NEBLINKANONYM', value: combinedString });
                quests.tags = [...quests.tags, ...allPatientLinksCombined];
                return quests;
            })
            .then((quests) =>
                dispatch(indexContracts({ customer_id: customerId })).then(
                    ({ data: contracts }) => {
                        quests.agencyIds = contracts.reduce((carry, contract) => {
                            if (contract.agencyId && !carry.includes(contract.agencyId)) {
                                carry.push(contract.agencyId);
                            }
                            return carry;
                        }, []);
                        // handleSent needs the care need to attach the created
                        // NurseRequests to (see the careNeedIds it sends below) -
                        // without it, requests are created without ever being
                        // linked back to the care need that requested them, so
                        // e.g. the care need never leaves the kanban board's
                        // "BK-Anfragen" column once its requests are closed.
                        quests.careNeedId = careNeedId;
                        return quests;
                    }
                )
            )
            .then((quests) => {
                if (!careNeedId) {
                    return quests;
                }

                // The {{BEGINN}} tag falls back to the customer's own
                // careStartAt/icStart, which is typically still unset while a
                // nurse is being requested. This care need's own care_from is
                // already set at this point (it's required on creation), so
                // prefer it - see config/wizard/tags.js.
                return dispatch(showCareNeed({ id: careNeedId })).then(({ data: careNeed }) => {
                    quests.careFrom = careNeed?.careFrom ?? null;
                    return quests;
                });
            });
    }, [customerId, careNeedId, dispatch]);

    const handleSent = useCallback(
        async ({ extraEntities }) => {
            const agencyIds = (extraEntities.agencies || []).filter(
                (agencyId) => agencyId !== null && agencyId !== undefined
            );

            if (agencyIds.length === 0) {
                // The email itself already went out - sending only needs a
                // resolved address. NurseRequest creation, though, is derived
                // from the raw receiver *values* (see SendMailForm's `agencies`
                // extraction): only receivers picked from the tagged
                // "agency_<id>" chip options count. A manually typed address,
                // or any other receiver kind, is silently excluded here - warn
                // instead of leaving the user thinking a request was created.
                enqueueSnackbar(
                    'E-Mail versendet, aber keine BK-Anfrage angelegt: kein ausgewählter Empfänger wurde als Agentur erkannt.',
                    { variant: 'warning' }
                );
                return undefined;
            }

            // Creates one NurseRequest per agency in a single request/DB
            // transaction, instead of a separate insert per agency.
            return dispatch(
                storeNurseRequests({
                    careNeedIds: [extraEntities.careNeedId],
                    customerId,
                    agencyIds,
                })
            );
        },
        [customerId, dispatch, enqueueSnackbar]
    );

    return { prepareExtraEntities, handleSent };
};

/**
 * Shared by SendNurseRequestCancelationButton (fresh send) and MailDraftActions
 * (continuing a saved draft): closes every open nurse request for the care need
 * and removes the care need itself once it has no deployment history.
 *
 * SendNurseRequestCancelationButton already receives the care need's nurse
 * requests via its nurseRequestData prop (loaded by the kanban card, available
 * synchronously) - pass them as nurseRequestsOverride to use those directly.
 * MailDraftActions has no such prop, so resolveNurseRequests dispatches a
 * fresh index call instead. It's dispatched at call time rather than read off
 * a `useNurseRequestsByCareNeedId` hook value, because that hook's fetch may
 * still be in flight when the user clicks "continue" right after opening a
 * draft - a stale/empty snapshot there would starve the agency receiver list
 * (and prepareExtraEntities/handleSent) of the still-loading result.
 */
export const useNurseRequestCancelationSend = (customerId, careNeedId, nurseRequestsOverride) => {
    const dispatch = useDispatch();
    const [nurseDeployments] = useNurseDeploymentsByCareNeedId(careNeedId);

    const resolveNurseRequests = useCallback(() => {
        if (nurseRequestsOverride) {
            return Promise.resolve(nurseRequestsOverride);
        }

        return dispatch(indexNurseRequests({ careNeedIds: careNeedId })).then(
            ({ data }) => data || []
        );
    }, [dispatch, nurseRequestsOverride, careNeedId]);

    const prepareExtraEntities = useCallback(
        () =>
            resolveNurseRequests().then((nurseRequests) => ({
                // nurseRequestsOverride (the kanban card's data) names this field
                // `agency`; the nurse_requests API resource names it `agencyId`.
                agencyIds: (nurseRequests || []).map(
                    (nurseRequest) => nurseRequest.agency ?? nurseRequest.agencyId
                ),
                careNeedId,
                customerId,
            })),
        [resolveNurseRequests, careNeedId, customerId]
    );

    const destroyEmptyCareNeed = useCallback(() => {
        // make sure there are no nurse_deployments for this care_need
        const nurseDeploymentIds = (nurseDeployments || []).filter(
            (deployment) => deployment.careNeedId === careNeedId
        );
        if (nurseDeploymentIds.length === 0) {
            dispatch(destroyCareNeed({ id: careNeedId }));
        }
    }, [dispatch, careNeedId, nurseDeployments]);

    const handleSent = useCallback(async () => {
        // Closes every open NurseRequest for this care need in a single
        // request/DB transaction, instead of a separate update per
        // NurseRequest (mirrors the storeBulk approach used when sending).
        const nurseRequests = await resolveNurseRequests();
        const ids = (nurseRequests || []).map((nurseRequest) => nurseRequest.id);

        const result = ids.length > 0 ? await dispatch(closeNurseRequests({ ids })) : undefined;

        // now close care need, related to the nurse requests -> only when EMPTY => with no history
        destroyEmptyCareNeed();

        return result;
    }, [dispatch, resolveNurseRequests, destroyEmptyCareNeed]);

    return { prepareExtraEntities, handleSent };
};
